// Page « Mentions légales et données » d'un club (D-103), sans compte, comme la page publique (D-031) :
// /c/<adresse>/donnees, /donnees sur le sous-domaine du club, <lien reçu>/donnees (invitation /i/, saisie
// des stats /s/, page partenaires d'un apporteur). Aussi dans l'app (Aide › « Mentions légales et données
// du club »). Le texte vient du modèle src/domain/legal.ts, rempli avec les réglages du bureau.
import { clubLegal } from '../lib/minors-legal';
import { clubDataSections, DRAFT_NOTE, MENTIONS_URL, PRIVACY_URL, type Block, type ClubLegal, type LegalSection, type Part } from '../domain/legal';
import { html, mount, type Html } from './dom';
import { applyBrand } from './theme';
import './legal.css';

const part = (p: Part): Html | string => (typeof p === 'string' ? p
  : 'b' in p ? html`<strong>${p.b}</strong>`
  : 'a' in p ? html`<a href="${p.href}" ${p.href.startsWith('http') ? html`target="_blank" rel="noopener"` : ''}>${p.a}</a>`
  : html`<span class="lg-todo">[${p.todo}]</span>`);
const block = (b: Block): Html => ('p' in b ? html`<p>${b.p.map(part)}</p>` : html`<ul>${b.ul.map(li => html`<li>${li.map(part)}</li>`)}</ul>`);
const section = (s: LegalSection, tag: 'h2' | 'h3' = 'h2'): Html => html`
  <section class="pub-sec lg-sec" id="lg-${s.id}">${tag === 'h2' ? html`<h2>${s.title}</h2>` : html`<h3>${s.title}</h3>`}${s.blocks.map(block)}</section>`;

/** Le contenu (bandeau « modèle », sections, liens vers les textes d'Arceau), commun à la page publique et à l'app. */
export function clubDataBody(c: ClubLegal, tag: 'h2' | 'h3' = 'h2'): Html {
  return html`
    <p class="lg-draft" role="note"><b>Modèle</b>${DRAFT_NOTE}</p>
    ${clubDataSections(c).map(s => section(s, tag))}
    <section class="pub-sec lg-sec lg-more" id="lg-plus">
      ${tag === 'h2' ? html`<h2>Pour aller plus loin</h2>` : html`<h3>Pour aller plus loin</h3>`}
      <ul>
        <li><a href="${PRIVACY_URL}" target="_blank" rel="noopener">Politique de confidentialité de l’app</a></li>
        <li><a href="${MENTIONS_URL}" target="_blank" rel="noopener">Mentions légales d’Arceau</a></li>
      </ul>
    </section>`;
}

/** La page publique. `back` : la page d'où l'on vient (la page du club, le lien reçu, l'espace membres). */
export async function renderClubData(root: HTMLElement, key: { slug?: string | null; token?: string | null },
  backOf: (c: ClubLegal) => { href: string; label: string }): Promise<void> {
  let c: ClubLegal | null = null;
  try { c = await clubLegal(key); } catch { /* réseau : même message */ }
  if (!c) {
    document.title = 'Page introuvable';
    mount(root, html`
      <main class="pub-missing">
        <h1>Page introuvable</h1>
        <p>Ce club n’a pas de page à cette adresse. Les règles de l’app : <a href="${PRIVACY_URL}">politique de confidentialité</a>.</p>
      </main>`);
    return;
  }
  const club = c;
  const back = backOf(club);
  applyBrand({ id: `donnees:${key.slug ?? key.token?.slice(0, 8) ?? ''}`, name: club.name, short_name: club.short_name, logo_url: club.logo, primary_color: club.color }, false);
  document.title = `Mentions légales et données · ${club.short_name || club.name}`;
  const logo = club.logo ? html`<img class="pub-logo" src="${club.logo}" alt="Logo du ${club.name}">`
    : html`<span class="pub-logo pub-mono" aria-hidden="true">${(club.short_name || club.name).slice(0, 3).toUpperCase()}</span>`;
  mount(root, html`
    <div class="pub lg-pub">
      <header class="pub-head">
        <div class="pub-id">${logo}<div><h1>Mentions légales et données</h1><p>${club.name}</p></div></div>
      </header>
      <main class="pub-main lg-main">${clubDataBody(club)}</main>
      <footer class="pub-foot">
        ${logo}
        <div><strong>${club.name}</strong><small>Mentions légales et données · propulsé par Arceau</small></div>
        <a class="pub-members" href="${back.href}">${back.label}</a>
      </footer>
    </div>`);
}
